import { useCallback, useRef } from 'react';
import { useStore } from '@nanostores/react';
import {
  assistantWidget,
  assistantMessages,
  assistantStreaming,
  assistantStreamBuffer,
  assistantPendingIntent,
  assistantSuggestions,
  assistantSelectInput,
} from '../stores/assistantStore';
import * as api from '../services/assistantApi';
import type { ChatMessage, StreamEvent } from '../types/assistant';

const FALLBACK_ERROR =
  'Sorry, something went wrong on my end and I could not finish that. Please try again.';

// Ends a turn with a visible message in the thread. Anything that stops a
// stream has to go through here: a cleared buffer with no message reads to the
// user as the assistant silently ignoring them, which is how a 500 on the
// streaming endpoint used to present (stuck on "Thinking...", then nothing).
function pushAssistantError(detail?: string) {
  assistantStreaming.set(false);
  assistantStreamBuffer.set('');
  assistantSelectInput.set(null);
  assistantMessages.set([
    ...assistantMessages.get(),
    {
      id: Date.now(),
      role: 'assistant',
      content: detail && detail.trim() ? detail : FALLBACK_ERROR,
      content_type: 'text',
      created_at: new Date().toISOString(),
    },
  ]);
}

// Singleton stream listener — registered once at module level so multiple
// widget instances (from different packs) don't duplicate events.
let streamListenerAttached = false;
function ensureStreamListener() {
  if (streamListenerAttached) return;
  streamListenerAttached = true;

  window.addEventListener('didi:stream', (e: Event) => {
    const event = (e as CustomEvent<StreamEvent>).detail;
    switch (event.type) {
      case 'token':
        assistantStreamBuffer.set(assistantStreamBuffer.get() + (event.token || ''));
        break;
      case 'complete':
        assistantStreaming.set(false);
        assistantStreamBuffer.set('');
        if (event.message) {
          assistantMessages.set([...assistantMessages.get(), event.message]);
        }
        if (event.suggestions) {
          assistantSuggestions.set(event.suggestions);
        } else {
          assistantSuggestions.set([]);
        }
        assistantSelectInput.set(event.select_input ?? null);
        break;
      case 'buffer_clear':
        assistantStreamBuffer.set('');
        break;
      case 'error':
        // Never fail silently: clearing the buffer without saying anything is
        // indistinguishable from the assistant ignoring the user.
        pushAssistantError(event.error);
        break;
    }
  });
}
ensureStreamListener();

export function useAssistantChat() {
  const widget = useStore(assistantWidget);
  const messages = useStore(assistantMessages);
  const streaming = useStore(assistantStreaming);
  const streamBuffer = useStore(assistantStreamBuffer);
  const pendingIntent = useStore(assistantPendingIntent);
  const abortRef = useRef<(() => void) | null>(null);

  const loadConversation = useCallback(async (conversationId: number) => {
    try {
      const data = await api.getConversation(conversationId);
      assistantMessages.set(data.messages);
      assistantSuggestions.set([]);
      assistantSelectInput.set(null);
      assistantWidget.setKey('activeConversationId', conversationId);
      assistantWidget.setKey('activePanel', 'chat');
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  }, []);

  const startNewConversation = useCallback(async () => {
    try {
      const conv = await api.createConversation();
      assistantMessages.set([]);
      assistantSuggestions.set([]);
      assistantSelectInput.set(null);
      assistantWidget.setKey('activeConversationId', conv.id);
      assistantWidget.setKey('activePanel', 'chat');
      return conv.id;
    } catch (err) {
      console.error('Failed to create conversation:', err);
      return null;
    }
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    let convId = widget.activeConversationId;
    if (!convId) {
      convId = await startNewConversation();
      if (!convId) return;
    }

    // Add user message optimistically
    const userMessage: ChatMessage = {
      id: Date.now(),
      role: 'user',
      content,
      content_type: 'text',
      created_at: new Date().toISOString(),
    };
    assistantMessages.set([...assistantMessages.get(), userMessage]);
    assistantStreaming.set(true);
    assistantStreamBuffer.set('');
    assistantSuggestions.set([]);
    assistantSelectInput.set(null);

    const intent = pendingIntent || undefined;
    assistantPendingIntent.set(null);

    const { abort, stream } = api.sendMessageStream(convId, content, undefined, intent);
    abortRef.current = abort;

    try {
      await stream;
      // A stream that ends without a 'complete' event (dropped connection,
      // a non-2xx that closed mid-flight) would otherwise leave the composer
      // disabled and the turn hanging on "Thinking..." forever.
      if (assistantStreaming.get()) pushAssistantError();
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Stream error:', err);
        pushAssistantError();
      } else {
        assistantStreaming.set(false);
      }
    }
  }, [widget.activeConversationId, pendingIntent, startNewConversation]);

  const cancelStream = useCallback(() => {
    abortRef.current?.();
    assistantStreaming.set(false);
    assistantStreamBuffer.set('');
  }, []);

  return {
    messages,
    streaming,
    streamBuffer,
    sendMessage,
    cancelStream,
    loadConversation,
    startNewConversation,
  };
}
